import { isAuthenticated } from "@app/utils/auth";
import { Form, message } from "antd";
import React, { FunctionComponent, useEffect, useMemo, useRef, useState } from "react";
import { captureException } from "@sentry/nextjs";
import { useRouter } from "next/router";
import { nanoid } from "nanoid";
import styles from "./BiformLayout.module.scss";
import FormSkeleton from "./FormSkeleton";
import FormCompleted from "./components/FormCompleted";
import { GeoCaptureAlreadySubmitted } from "./components/GeoCaptureForm/GeoCaptureCompleted";
import GeoCaptureForm from "./components/GeoCaptureForm/GeoCaptureForm";
import GeoCaptureSkeleton from "./components/GeoCaptureForm/GeoCaptureSkeleton";
import Result from "../Result/Result";
import InfyButton from "../InfyButton/InfyButton";
import CourseFeedbackForm, { CourseFeedbackSkeleton } from "./components/CourseFeedbackForm";
import JobForm from "./components/JobForm";
import JobFormSkeleton from "./components/JobForm/JobFormSkeleton";
import OffCampusRequestForm from "./components/OffCampusRequestForm/OffCampusRequestForm";
import OffCampusRequestSkeleton from "./components/OffCampusRequestForm/OffCampusRequestSkeleton";
import {
  fetchLearnerResponsesForAdmin,
  fetchLearnerResponsesForAdminForSessionFeedbackForm,
  getFormDataById,
  getLearnerFeedbackResponse,
  getLearnerResponseByFormId,
  saveDraftResponse,
  submitAdminResponse,
  submitLearnerResponse
} from "./apis";
import { buildFieldValuesFromDraft, buildResponses } from "./formUtils";
import getQuestionComponent from "./getQuestionComponent";
import { clientErrorMessage, resolveLoadErrorMessage, V2FormData, V2Question, V2Response } from "./helper";
import OfficeLocationQuestion from "./components/OfficeLocationQuestion";
import {
  applyWeekZeroRequired,
  isCompletionCertificateQuestion,
  WEEK_ZERO_MENTOR_EMAIL_MARKER,
  defaultWeekZeroModeValue,
  findWeekZeroQuestion,
  orderWeekZeroQuestions,
  isOfficeMode,
  questionsForSubmission,
  trimOfficeLocationAnswer,
  WEEK_ZERO_MODE_MARKER,
  WEEK_ZERO_OFFICE_URL_MARKER
} from "./weekZeroLocation";

/** The longest the already-submitted pre-check may hold the form skeleton; past it the form opens as if not submitted. */
const ALREADY_SUBMITTED_CHECK_MS = 8000;

/**
 * FormBuilder renders a v2 BI form directly from its `questions[]` array.
 *
 *  - v3 `COURSE_FEEDBACK` → dedicated `CourseFeedbackForm` UI.
 *  - everything else      → each question rendered by `getQuestionComponent` inside an antd `Form`.
 *
 * On submit, antd form values are converted to the v2 `{ questionId, answer }[]` payload via
 * `buildResponses`; saved drafts are pre-filled via `buildFieldValuesFromDraft`.
 */
const FormBuilder: FunctionComponent<{ parameters: any }> = ({ parameters }) => {
  const router = useRouter();
  const [form] = Form.useForm();

  // ----- State -----
  const [formData, setFormData] = useState<V2FormData | null>(null);
  const [draftData, setDraftData] = useState<{ responses: V2Response[]; status: string | null; hiddenQuestionIds?: string[] } | null>(null);
  const [loading, setLoading] = useState(true);
  // Set instead of navigating away whenever this is running inside bi-main's iframe — a coach
  // should never see the standalone login screen embedded in a feedback form.
  const [loadError, setLoadError] = useState<string | null>(null);
  const [formSubmitted, setFormSubmitted] = useState(false);
  // The learner has a completed submission and the form does not accept another.
  const [alreadySubmitted, setAlreadySubmitted] = useState(false);
  const [responseScore, setResponseScore] = useState(null);
  const [btnLoading, setBtnLoading] = useState({ draftBtn: false, submitBtn: false });
  const [formId] = useState(parameters.formid || parameters.formId);

  const formRef = useRef(null);

  // `source` is either "user" (learner) or "admin".
  const {
    formType,
    hideHeaders,
    source,
    // userid,
    // coachid,
    // studentid,
    draft,
    hideButtons = false,
    componentId = undefined,
    sessionid = undefined,
    // programid = undefined,
    // sectionid = undefined,
    adminEmail = undefined,
    adminName = undefined,
    // FEEDBACK forms: who is giving feedback to whom (supplied as explicit query params).
    givenByUserType = undefined,
    givenByUserId = undefined,
    givenToUserType = undefined,
    givenToUserId = undefined,

    // userid = undefined,

    // to distinguish who is submitting i.e admin or coach
    relatedModelName = undefined,

    submissionId = undefined
  } = parameters;

  const isCourseFeedback = formType === "COURSE_FEEDBACK";
  // Geo capture gets its own layout: the photo leads, and the camera/location permissions are
  // surfaced on arrival rather than discovered inside the capture modal. `formType` is the URL hint
  // (available immediately, so the skeleton matches); `formData.type` is the authoritative answer.
  const isGeoCapture = formType === "GEO_CAPTURE" || formData?.type === "GEO_CAPTURE";
  // `hideButtons` marks a read-only view (the builder preview, or the admin's view of a response with
  // `submissionId`). The capture layout is a live camera with Submit, so those views use the generic
  // layout instead, which renders a GEO_PICTURE answer as the photo with its coordinates (read-only) or "Location recorded".
  const isGeoCaptureLayout = isGeoCapture && !hideButtons;
  // Off-campus requests get their own layout too: Company / Role are picked from the master list and
  // the seeded questions are found by their `metadata.internshipField` marker. Same hint-or-type rule.
  const isOffCampusRequest = formType === "OFF_CAMPUS_REQUEST" || formData?.type === "OFF_CAMPUS_REQUEST";
  // Job-form surveys (SURVEY + settings.isJobForm) get a totally separate layout. `formData` is only
  // available after load, so this resolves once the form is fetched (the loading guard runs first).
  const isJobForm = (formType === "SURVEY" || formData?.type === "SURVEY") && Boolean((formData?.settings as { isJobForm?: boolean })?.isJobForm);
  // `isJobForm` isn't known until the form is fetched, so during loading we rely on a URL hint
  // (?isJobForm=true) — the same approach the isCourseFeedback skeleton uses via `formType`.
  const isJobFormHint = parameters?.isJobForm === "true" || parameters?.isJobForm === true;

  // Query params arrive as `string | string[]` — a repeated key (e.g. givenByUserType=LEARNER&givenByUserType=LEARNER)
  // becomes an array. The user types are single-valued, so collapse any array down to its first entry.
  const firstValue = (value: unknown) => (Array.isArray(value) ? value[0] : value);
  const feedbackMeta = {
    givenByUserType: firstValue(givenByUserType),
    givenByUserId: firstValue(givenByUserId),
    givenToUserType: firstValue(givenToUserType),
    givenToUserId: firstValue(givenToUserId)
  };
  const formStatus = draftData?.status ?? null;

  // Questions whose answers the server stripped from this read: the form marks them
  // not-visible-to-student and the reader is the student the feedback is about. The form definition
  // comes from an unauthenticated endpoint and so still lists every question — without the server's
  // list of ids to skip, the hidden question renders with a permanently empty answer.
  const hiddenQuestionIds = useMemo(() => new Set(draftData?.hiddenQuestionIds || []), [draftData]);

  // Questions rendered in their defined order (Week 0 details form: mode, then office location, then the rest).
  const questions: V2Question[] = useMemo(
    () =>
      orderWeekZeroQuestions(
        [...(formData?.questions || [])]
          .filter(question => !hiddenQuestionIds.has(question.id))
          .map(question => (hideButtons ? question : applyWeekZeroRequired(question)))
          .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
      ),
    [formData, hiddenQuestionIds, hideButtons]
  );

  // ----- Data fetching -----
  const getFormData = async () => {
    try {
      const parsedUrl = new URL(window.location.href);
      const pathWithQuery = parsedUrl.pathname + parsedUrl.search;

      // Learner submissions require an authenticated session. When embedded, show an error instead
      // of redirecting to the standalone login screen; standalone visits still redirect as before.
      // Asks the readable `authState` flag, not the token: the token cookie is HttpOnly, so reading
      // it would report every signed-in learner as signed out.
      if (source === "user" && !isAuthenticated()) {
        const isEmbedded = typeof window !== "undefined" && window.parent !== window;
        if (isEmbedded) {
          setLoadError("You don't have permission to view this feedback form.");
          setLoading(false);
        } else {
          router.push(`/login?callbackUrl=${pathWithQuery}`);
        }
        return;
      }

      const response = await getFormDataById(formId);
      setFormData(response?.data || null);

      // The form definition comes from an unauthenticated endpoint, so it cannot say whether THIS
      // learner has already answered. Ask the authenticated endpoint, which computes `canSubmit`
      // with the same rule the submit guard applies. Without this a learner on a single-submission
      // form gets a blank form, fills it in, and only discovers the rejection after submitting —
      // which on a geo-capture form means a wasted photo upload too.
      //
      // Fails open: the backend still rejects a disallowed resubmission, so an error here (a
      // PROGRAM_SECTION form needs query params this call does not send) must not block the form.
      //
      // Skipped when the answer would not describe this link: a session-context link (session
      // feedback is stored per session, giver and receiver, not per form, so a per-form `canSubmit`
      // says nothing about this session), and an off-campus request, whose layout reads the
      // learner's existing submission itself and would only fetch it a second time.
      const loadedType = response?.data?.type;
      const hasSessionContext =
        Boolean(sessionid || parameters.sessionId) ||
        (relatedModelName || parameters.relatedModelname) === "SESSION" ||
        [formType, loadedType].some(type => type === "SESSION_FEEDBACK" || type === "FEEDBACK");
      const checksOwnSubmission = formType === "OFF_CAMPUS_REQUEST" || loadedType === "OFF_CAMPUS_REQUEST";
      //
      // Also skipped for a read-only view (`hideButtons`), COURSE_FEEDBACK and job forms, which keep
      // their own submission handling. Started here but awaited after the draft load so the two run in
      // parallel. It fails open and holds the form skeleton for at most 8 s: the race below caps it, since
      // the shared API client may retry a timed-out request on the configured host (another 8 s).
      const loadedSettings = response?.data?.settings as { isJobForm?: boolean } | undefined;
      const isCourseFeedbackForm = [formType, loadedType].some(type => type === "COURSE_FEEDBACK");
      const isJobFormLoaded = loadedType === "SURVEY" && Boolean(loadedSettings?.isJobForm);
      const shouldCheckSubmission =
        source === "user" && !hideButtons && !hasSessionContext && !checksOwnSubmission && !isCourseFeedbackForm && !isJobFormLoaded;
      const alreadySubmittedCheck: Promise<boolean> = shouldCheckSubmission
        ? Promise.race([
            getLearnerResponseByFormId(formId, ALREADY_SUBMITTED_CHECK_MS).then(
              learnerState => learnerState?.data?.canSubmit === false,
              () => false
            ),
            new Promise<boolean>(resolve => {
              setTimeout(() => resolve(false), ALREADY_SUBMITTED_CHECK_MS);
            })
          ])
        : Promise.resolve(false);

      // Load any previously saved draft / submission so the form can be pre-filled.
      if (draft) {
        if (source === "admin") {
          if (formType === "SESSION_FEEDBACK" || response?.data?.type === "SESSION_FEEDBACK") {
            const adminResponse = await fetchLearnerResponsesForAdminForSessionFeedbackForm({
              sessionId: sessionid,
              givenByUserId,
              givenToUserId
            });
            setDraftData({ responses: adminResponse?.data?.responses || [], status: adminResponse?.data?.status ?? null });
          } else {
            const adminResponse = await fetchLearnerResponsesForAdmin(formId, submissionId);
            setDraftData({ responses: adminResponse?.data?.responses || [], status: adminResponse?.data?.status ?? null });
          }
        } else {
          const learnerResponse = await getLearnerFeedbackResponse({
            sessionId: sessionid,
            givenByUserId,
            givenToUserId
          });
          // The feedback-response endpoint returns the submission object directly under `data`
          // ({ id, formId, status, responses: [{ questionId, answer }], ... }) — no `existingSubmission` wrapper.
          const submission = learnerResponse?.data;
          setDraftData({
            responses: submission?.responses || [],
            status: submission?.status ?? null,
            // Present only on the receiver's read; absent (so nothing hidden) when the coach reads their own draft.
            hiddenQuestionIds: submission?.hiddenQuestionIds || []
          });
        }
      }

      if (await alreadySubmittedCheck) setAlreadySubmitted(true);

      setLoading(false);

      // if (response?.data) return;
      // router.push("/404");
    } catch (err) {
      captureException(err);
      setLoadError(resolveLoadErrorMessage(err));
      setLoading(false);
    }
  };

  // Fetch the form on mount and listen for an external "submit" trigger (iframe host).
  useEffect(() => {
    if (!formId) {
      router.push("/404");
    }

    getFormData();

    if (window) {
      window.addEventListener("message", event => {
        if (event.data === "triggerButtonClick") {
          handleOutsideButtonClick();
        }
      });
    }
  }, []);

  // Pre-fill the form fields once a draft / existing submission is loaded.
  useEffect(() => {
    if (!draftData?.responses?.length || !questions.length) return;
    form.setFieldsValue(buildFieldValuesFromDraft(questions, draftData.responses));
  }, [draftData, questions]);

  // Week 0 details form: a fillable form with no saved answer for the mode starts on Offline. Runs once, after the load
  // (so the draft effect above has already pre-filled), and only while the field is still empty, so a draft or a
  // submitted answer is never overwritten. Skipped for read-only views (`hideButtons`). No-op without the mode marker.
  const weekZeroDefaultApplied = useRef(false);
  useEffect(() => {
    if (loading || weekZeroDefaultApplied.current || hideButtons || !questions.length) return;
    weekZeroDefaultApplied.current = true;
    const defaultMode = defaultWeekZeroModeValue(questions);
    if (!defaultMode) return;
    const saved = draftData?.responses?.find(response => response.questionId === defaultMode.questionId);
    const hasSavedAnswer = saved && saved.answer !== null && saved.answer !== undefined && saved.answer !== "";
    const current = form.getFieldValue(defaultMode.questionId);
    if (hasSavedAnswer || (current !== undefined && current !== null && current !== "")) return;
    form.setFieldsValue({ [defaultMode.questionId]: defaultMode.value });
  }, [loading, questions, draftData, hideButtons]);

  // ----- Submission -----
  const handleDraft = async () => {
    const isSessionFeedback = formType === "SESSION_FEEDBACK" || formData?.type === "SESSION_FEEDBACK";
    const draftValues = form.getFieldsValue(true);
    const responses = trimOfficeLocationAnswer(questions, buildResponses(questionsForSubmission(questions, draftValues), draftValues));
    setBtnLoading({ ...btnLoading, draftBtn: true });
    try {
      await saveDraftResponse(formId, {
        sessionId: sessionid,
        responses,
        ...(isSessionFeedback && { feedbackMeta })
      });
      window?.parent?.postMessage({ action: "formSavedInDraft" }, "*");
    } catch (err) {
      captureException(err);
      router.push("/error");
    } finally {
      setBtnLoading({ ...btnLoading, draftBtn: false });
    }
  };

  const onFinish = async (values: Record<string, unknown>) => {
    const responses = trimOfficeLocationAnswer(questions, buildResponses(questionsForSubmission(questions, values), values));
    setBtnLoading({ ...btnLoading, submitBtn: true });
    const isInvideoQuiz = formData?.type === "VIDEO_BASED";
    const isSessionFeedback = formType === "SESSION_FEEDBACK" || formData?.type === "SESSION_FEEDBACK";
    try {
      if (source === "admin") {
        await submitAdminResponse(formId, {
          submittedByEmail: adminEmail,
          submittedByName: adminName,
          sessionId: sessionid,
          responses,
          relatedModelName,
          ...(isSessionFeedback && { feedbackMeta })
        });
      } else {
        const res = await submitLearnerResponse(formId, {
          responses,
          componentId,
          sessionId: sessionid,
          relatedModelName,
          ...(isSessionFeedback && { feedbackMeta })
        });

        // In-video quizzes return a score that is surfaced via the Result screen.
        if (res && isInvideoQuiz) {
          const scoreResponse = await getLearnerResponseByFormId(formId);
          const score = scoreResponse?.data?.existingSubmission?.responseScores;
          if (score) {
            setResponseScore(score);
          }
        }
      }
      if (!isInvideoQuiz) setFormSubmitted(true);
      window?.parent?.postMessage({ action: "formSubmitted" }, "*");
    } catch (err) {
      // Completion certificate / Week 0 mentor email forms: the server's HTTP 400 reason is shown as-is and the learner stays on the form.
      const reason = clientErrorMessage(err);
      const hasServerCheckedField = questions.some(
        question => isCompletionCertificateQuestion(question) || findWeekZeroQuestion([question], WEEK_ZERO_MENTOR_EMAIL_MARKER)
      );
      if (reason && hasServerCheckedField) {
        message.error(reason);
        return;
      }
      captureException(err);
      window?.parent?.postMessage({ action: "somethingWentWrong" }, "*");
      router.push("/error");
    } finally {
      setBtnLoading({ ...btnLoading, submitBtn: false });
    }
  };

  // Week 0 details form: the office link only applies to an Offline / Hybrid mode, so drop its text as soon as
  // the mode stops being one (Online, or cleared). No-op for every form without the two markers.
  const onValuesChange = (changedValues: Record<string, unknown>) => {
    const mode = findWeekZeroQuestion(questions, WEEK_ZERO_MODE_MARKER);
    const office = findWeekZeroQuestion(questions, WEEK_ZERO_OFFICE_URL_MARKER);
    if (mode && office && mode.id in changedValues && !isOfficeMode(changedValues[mode.id])) {
      form.setFieldsValue({ [office.id]: undefined });
    }
  };

  const onFinishFailed = ({ errorFields }) => {
    if (errorFields.length > 0) {
      window?.parent?.postMessage({ action: "errorFound" }, "*");

      // Scroll the first invalid field into view.
      const fieldId = errorFields[0]?.name[0];
      const errorField = document.getElementById(fieldId);
      if (errorField) {
        errorField.scrollIntoView({ behavior: "smooth" });
      }
    }
  };

  // Allows an iframe host to submit the form via a postMessage trigger.
  const handleOutsideButtonClick = () => {
    if (formRef.current) {
      formRef.current.submit();
    }
  };

  // ----- Render -----
  if (loadError) {
    return <div className="py-6 px-5 text-sm text-gray-500">{loadError}</div>;
  }

  if (loading) {
    if (isCourseFeedback) return <CourseFeedbackSkeleton />;
    if (isGeoCaptureLayout) return <GeoCaptureSkeleton />;
    if (isOffCampusRequest) return <OffCampusRequestSkeleton />;
    if (isJobFormHint) return <JobFormSkeleton />;
    return (
      <div className={styles.loader}>
        <FormSkeleton />
      </div>
    );
  }

  if (responseScore) {
    return <Result responseScore={responseScore} />;
  }

  if (isCourseFeedback) {
    return <CourseFeedbackForm formData={formData} formId={formId} courseId={parameters.courseId || null} />;
  }

  if (isJobForm) {
    return <JobForm formData={formData} formId={formId} parameters={parameters} />;
  }

  // Above the already-submitted gate, like the job form: it reads the learner's existing submission
  // itself, and also serves the builder preview and the admin's view of a response.
  if (isOffCampusRequest) {
    return <OffCampusRequestForm formData={formData} formId={formId} parameters={parameters} />;
  }

  if (formSubmitted || alreadySubmitted) {
    // The geo capture layout's own "Already Submitted" screen, with the tenant logo like the capture form.
    if (isGeoCaptureLayout && !formSubmitted) return <GeoCaptureAlreadySubmitted />;
    return <FormCompleted alreadySubmitted={!formSubmitted && alreadySubmitted} />;
  }

  // Below the already-submitted gate on purpose: a learner who cannot submit again should see that,
  // not a capture screen inviting them to take a photo they cannot send.
  if (isGeoCaptureLayout) {
    return <GeoCaptureForm formData={formData} formId={formId} parameters={parameters} />;
  }

  return (
    <div className={styles.biFormContainer}>
      <div className={`${styles.innerContainer} ${hideButtons ? styles.fullHeight : ""}`}>
        <Form
          ref={formRef}
          requiredMark={false}
          layout="vertical"
          form={form}
          name="register"
          onFinish={onFinish}
          onFinishFailed={onFinishFailed}
          onValuesChange={onValuesChange}
          scrollToFirstError
        >
          <div className={styles.scroll}>
            {!hideHeaders && (
              <>
                {(parameters?.formtitle || formData?.title) && <h1 className={styles.title}>{parameters?.formtitle || formData?.title}</h1>}
                {formData?.description && <h1 className={styles.description}>{formData.description}</h1>}
              </>
            )}
            {questions.map(question =>
              (question.metadata as { weekZeroField?: string } | null | undefined)?.weekZeroField === WEEK_ZERO_OFFICE_URL_MARKER ? (
                <OfficeLocationQuestion key={question.id || nanoid()} question={question} questions={questions} disabled={hideButtons} />
              ) : (
                <div className={styles.formComponents} key={question.id || nanoid()}>
                  {getQuestionComponent({
                    question,
                    disabled: hideButtons,
                    formType: formType || formData?.type,
                    legacyId: formData?.metadata?.legacyId
                  })}
                </div>
              )
            )}
          </div>

          {!hideButtons && (
            <div className="flex items-center justify-end gap-4 mt-4">
              {source !== "admin" && draft && formStatus !== "SUBMITTED" && (
                <InfyButton type="ghost" loading={btnLoading.draftBtn} bicolor="primary" onClick={handleDraft}>
                  Save As Draft
                </InfyButton>
              )}

              <InfyButton loading={btnLoading.submitBtn} type="primary" htmlType="submit" bicolor="primary">
                Submit
              </InfyButton>
            </div>
          )}
        </Form>
      </div>
    </div>
  );
};

export default FormBuilder;
